import Icon from "./Icon";
import "./geofence.css";

/**
 * Full-screen blocking overlay shown to an employee whose device is outside
 * their assigned geofence (or can't report a location). The parent keeps
 * polling and drives `checking` and `secondsLeft`; this clears automatically
 * when they return inside.
 *
 * Four distinct reasons, because the advice differs:
 *   checking             — the server locked us but we haven't worked out why yet
 *   location_required    — permission is blocked; only the user can clear it
 *   location_unavailable — permission is fine, we just couldn't get a fix
 *   outside (default)    — we know where they are, and it's out of bounds
 */
export default function GeofenceLockout({ reason, checking = false, secondsLeft, onRetry, onLogout }) {
  // Never claim someone is outside before we know it — a fence assigned
  // mid-session locks the server side first, and the real cause may turn out
  // to be a blocked permission, which needs opposite advice.
  const verifying = reason === "checking";
  const blocked = reason === "location_required";
  const noFix = reason === "location_unavailable";
  const needsLocation = blocked || noFix;

  const title = verifying
    ? "Confirming your location…"
    : blocked
      ? "Location access is blocked"
      : noFix
        ? "Can't read your location"
        : "You are outside your designated location";

  const body = verifying
    ? "Your admin has set a location restriction for your account. We're confirming where you are — this only takes a moment."
    : blocked
      ? "We need your location to confirm you're within your assigned area. Allow location access, then tap Check again — you'll be let straight back in."
      : noFix
        ? "Your location permission is fine, but we couldn't get a fix just now. Move near a window or step outside, make sure Location is switched on, then tap Check again."
        : "Your account is restricted to an area set by your admin. Return to your designated location to continue — this screen clears itself the moment you're back inside.";

  return (
    <div className="geo-lock" role="alertdialog" aria-modal="true">
      <div className="geo-lock-card">
        <div className="geo-lock-icon">
          <Icon name={needsLocation || verifying ? "map-pin" : "alert-triangle"} size={26} />
        </div>
        <div className="geo-lock-title">{title}</div>
        <div className="geo-lock-body">{body}</div>

        {/* Installed to the home screen there's no address bar and no site
            settings, and the in-app prompt never fires twice — so pointing at
            "allow for this site" would send them somewhere that doesn't exist. */}
        {blocked && (
          <div className="geo-lock-hint">
            <div><b>Android</b> — Settings › Apps › SMART CA › Permissions › Location</div>
            <div><b>iPhone</b> — Settings › SMART CA › Location</div>
            <div style={{ marginTop: 6 }}>
              In a browser tab, use the padlock in the address bar instead.
            </div>
          </div>
        )}

        <div className="geo-lock-actions">
          <button className="btn btn-primary" onClick={onRetry} disabled={checking}>
            {checking ? (
              <><span className="spinner spinner-sm" /> Checking…</>
            ) : (
              <><Icon name="refresh" size={14} /> Check again</>
            )}
          </button>
          {onLogout && (
            <button className="btn btn-secondary" onClick={onLogout} disabled={checking}>
              <Icon name="log-out" size={14} /> Sign out
            </button>
          )}
        </div>
        <div className="geo-lock-status">
          {checking ? (
            <><span className="spinner spinner-sm" /> Checking your location…</>
          ) : (
            <>
              <Icon name="clock" size={12} />{" "}
              {/* The `checking` branch takes over while a check is in flight,
                  so this never shows a stale or negative number. */}
              {typeof secondsLeft === "number"
                ? `Re-checking in ${secondsLeft}s…`
                : "Re-checking automatically…"}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
